import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ScanPaper from './ScanPaper';
import { request, useOperations } from './OperationsProvider';
import { documentState } from './documents';

// Recording a renewed document, which is the other half of recording an expiry.
//
// A truck's insurance, permit and PUC all lapse inside a year or two and come back renewed. Until
// this existed the new date had nowhere to go: the desk would ask for a waiver reason on every
// admission of that truck forever, and the morning briefing would report an expiry nobody could
// clear. The form opens on what is already recorded, so renewing one document is changing one date
// rather than re-entering the set.
export const DOCUMENT_KINDS = ['Registration', 'Insurance', 'PUC', 'Permit'];

const RenewDocuments = ({ vehicle, onClose }) => {
  const { refresh } = useOperations();
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState('');

  useEffect(() => {
    if (!vehicle) return;
    setError('');
    setRows(DOCUMENT_KINDS.map((kind) => {
      const held = (vehicle.documents || []).find((d) => d.kind === kind);
      return {
        kind, reference: held?.reference || '', expiresOn: held?.expiresOn || '', imageDataUrl: '', hadImage: !!held?.hasImage,
      };
    }));
  }, [vehicle]);

  const set = (kind, key, value) => setRows((current) => current.map((row) => (row.kind === kind ? { ...row, [key]: value } : row)));
  // A document is sent only when it carries both halves. A kind left blank is not cleared — it is
  // simply not mentioned, and the server leaves whatever was there alone.
  const filled = rows.filter((row) => row.reference.trim() && row.expiresOn)
    .map((row) => ({
      kind: row.kind, reference: row.reference.trim(), expiresOn: row.expiresOn, ...(row.imageDataUrl ? { imageDataUrl: row.imageDataUrl } : {}),
    }));
  const half = rows.some((row) => !!row.reference.trim() !== !!row.expiresOn);

  const save = async () => {
    setPending(true); setError('');
    try {
      await request(`/api/operations/vehicles/${vehicle.id}/documents`, 'PATCH', { documents: filled });
      await refresh();
      onClose();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  return (
    <Dialog open={!!vehicle} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{vehicle ? `Documents · ${vehicle.name}` : 'Documents'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Record the renewed reference and validity date. Dates are taken as entered — nothing is checked against an external register — and your name is kept with each one.
          </Typography>
          {rows.map((row) => {
            const state = row.expiresOn ? documentState(row) : 'unknown';
            return (
              <Stack key={row.kind} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  disabled={pending}
                  label={`${row.kind} reference`}
                  value={row.reference}
                  inputProps={{ maxLength: 100 }}
                  onChange={(e) => set(row.kind, 'reference', e.target.value)}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  disabled={pending}
                  label="Valid until"
                  InputLabelProps={{ shrink: true }}
                  value={row.expiresOn}
                  onChange={(e) => set(row.kind, 'expiresOn', e.target.value)}
                  error={state === 'expired'}
                  helperText={state === 'expired' ? 'This date has already passed.' : state === 'expiring' ? 'Expires within a fortnight.' : ' '}
                />
                <Button
                  size="small"
                  disabled={pending}
                  startIcon={<PhotoCameraIcon />}
                  sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                  onClick={() => setScanning(row.kind)}
                >
                  {row.imageDataUrl ? 'Rescan' : 'Scan'}
                </Button>
              </Stack>
            );
          })}
          {half && <Alert severity="info">A document needs both its reference and its validity date. Leave both blank to keep what is already recorded.</Alert>}
          {rows.some((row) => row.imageDataUrl) && (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              {rows.filter((row) => row.imageDataUrl).map((row) => (
                <Box key={row.kind} sx={{ textAlign: 'center' }}>
                  <Box component="img" src={row.imageDataUrl} alt={`${row.kind} as scanned`} sx={{ width: 84, height: 56, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }} />
                  <Typography variant="caption" color="text.secondary">{row.kind}</Typography>
                </Box>
              ))}
            </Stack>
          )}
          <ScanPaper
            kind={scanning}
            open={!!scanning}
            onClose={() => setScanning('')}
            onScanned={({ imageDataUrl, expiresOn, reference }) => setRows((current) => current.map((row) => (row.kind === scanning
              ? {
                ...row,
                imageDataUrl,
                expiresOn: expiresOn || row.expiresOn,
                // A tapped reference replaces what is there; leaving the chips alone keeps it.
                reference: reference ?? row.reference,
              }
              : row)))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending || half || !filled.length} onClick={save}>{pending ? 'Saving…' : 'Save documents'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewDocuments;
